import { ProductOutboundTransactionDoc, ProductTransactionStatus } from '@gooduncles/gu-app-schema';
import { ColDef, ITooltipParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';

import {
  cellStyleForDate,
  formatNumberForAgCell,
  getCellStyleForTransactionStatus,
  outboundReferenceCollectionFormatter,
  productOutboundOperationFormatter,
  productTransactionStatusFormatter,
  stringDateFormatter,
} from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgCellInventory from 'src/atomic-components/atoms/AgCell/AgCellInventory';
import AgCellProduct from 'src/atomic-components/atoms/AgCell/AgCellProduct';
import AgCellStore from 'src/atomic-components/atoms/AgCell/AgCellStore';

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

interface InventoryOutboundHistoryTableProps {
  rowData: ProductOutboundTransactionDoc[];
  hideReferenceCollection?: boolean;
}

const InventoryOutboundHistoryTable: FC<InventoryOutboundHistoryTableProps> = ({
  rowData,
  hideReferenceCollection,
}) => {
  const columnDefs: ColDef<ProductOutboundTransactionDoc>[] = useMemo(
    () => [
      {
        headerName: 'Id',
        field: '_id',
        width: 80,
        hide: true,
      },
      {
        headerName: 'inventoryId',
        field: 'inventoryId',
        width: 80,
        hide: true,
      },
      {
        headerName: '출고일',
        field: 'requestDate',
        width: 120,
        valueFormatter: stringDateFormatter,
        cellStyle: cellStyleForDate,
      },
      {
        headerName: '출고 경로',
        field: 'referenceCollection',
        width: 120,
        hide: hideReferenceCollection,
        valueFormatter: outboundReferenceCollectionFormatter,
        filterParams: {
          valueFormatter: outboundReferenceCollectionFormatter,
        },
      },
      {
        headerName: '출고 방식',
        field: 'operation',
        width: 120,
        valueFormatter: productOutboundOperationFormatter,
        filterParams: {
          valueFormatter: productOutboundOperationFormatter,
        },
      },
      {
        headerName: '재고명',
        field: 'inventoryName',
        width: 240,
        cellRenderer: AgCellInventory,
        cellRendererParams: (params: any) => ({
          inventoryId: params.data?.inventoryId,
          field: 'inventoryName',
        }),
      },
      {
        headerName: '변경량',
        field: 'requestQuantity',
        width: 100,
        valueFormatter: formatNumberForAgCell,
        type: 'numericColumn',
        cellRenderer: (params: any) => {
          const formattedValue = params.value === Infinity ? '∞' : formatNumber(params.value);
          const status: ProductTransactionStatus = params.data?.status;
          return <span className={`stock-cell outbound ${status}`}>{formattedValue}</span>;
        },
      },
      {
        headerName: '매장',
        field: 'storeNickname',
        width: 100,
        cellRenderer: AgCellStore,
        cellRendererParams: (params: any) => ({
          storeId: params.data?.storeId,
          field: 'storeNickname',
        }),
      },
      {
        headerName: '상품',
        field: 'fullName',
        width: 240,
        cellRenderer: AgCellProduct,
        cellRendererParams: (params: any) => ({
          productId: params.data?.productId,
          field: 'fullName',
        }),
      },
      {
        headerName: '상태',
        field: 'status',
        width: 80,
        valueFormatter: productTransactionStatusFormatter,
        cellStyle: getCellStyleForTransactionStatus,
        filterParams: {
          valueFormatter: productTransactionStatusFormatter,
        },
        tooltipValueGetter: (params: ITooltipParams<ProductOutboundTransactionDoc, ProductTransactionStatus>) => {
          if (
            params.value &&
            [ProductTransactionStatus.REJECTED, ProductTransactionStatus.FAILED].includes(params.value)
          ) {
            return params.data?.error;
          }
          return null;
        },
      },
      {
        headerName: '액션',
        minWidth: 120,
        cellRenderer: DownloadRawDataCell,
        hide: true,
      },
    ],
    [hideReferenceCollection]
  );

  return (
    <div className='ag-theme-alpine height100'>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableCellTextSelection={true}
        tooltipShowDelay={500}
      />
    </div>
  );
};

export default InventoryOutboundHistoryTable;
